import React, { useState, useEffect } from 'react';
import AdminNav from '../components/AdminNav';
import { Plus, Edit2, Trash2, X, Upload, LogOut, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { siteContentService, authService } from '../services/api';

function AdminScientificAdvisors() {
  const navigate = useNavigate();
  const [advisors, setAdvisors] = useState([]);

  useEffect(() => {
    const fetchAdvisors = async () => {
      try {
        const response = await siteContentService.get('SCIENTIFIC_ADVISORS');
        if (response.data && response.data.value) {
          const parsed = JSON.parse(response.data.value);
          if (Array.isArray(parsed)) {
            setAdvisors(parsed);
          }
        }
      } catch (error) {
        console.error('Error fetching scientific advisors:', error);
      }
    };
    fetchAdvisors();
  }, []);

  const saveAdvisors = async (updatedAdvisors) => {
    try {
      await siteContentService.set('SCIENTIFIC_ADVISORS', { value: JSON.stringify(updatedAdvisors) });
    } catch (error) {
      console.error('Error saving scientific advisors:', error);
    }
  };

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    specialty: '',
    email: '',
    phone: '',
    bio: '',
    image: null,
  });
  const [imagePreview, setImagePreview] = useState(null);

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let updatedAdvisors;
    if (editingId) {
      updatedAdvisors = advisors.map(a =>
        a.id === editingId
          ? { ...a, ...formData, image: imagePreview || a.image }
          : a
      );
      setEditingId(null);
    } else {
      const newAdvisor = {
        id: Math.max(...advisors.map(a => a.id), 0) + 1,
        ...formData,
        image: imagePreview,
      };
      updatedAdvisors = [...advisors, newAdvisor];
    }

    setAdvisors(updatedAdvisors);
    saveAdvisors(updatedAdvisors);

    setFormData({
      name: '',
      specialty: '',
      email: '',
      phone: '',
      bio: '',
      image: null,
    });
    setImagePreview(null);
    setShowForm(false);
  };

  const handleEdit = (advisor) => {
    setFormData({
      name: advisor.name,
      specialty: advisor.specialty,
      email: advisor.email,
      phone: advisor.phone,
      bio: advisor.bio,
      image: advisor.image,
    });
    setImagePreview(advisor.image);
    setEditingId(advisor.id);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('آیا مطمئن هستید؟')) {
      const updatedAdvisors = advisors.filter(a => a.id !== id);
      setAdvisors(updatedAdvisors);
      saveAdvisors(updatedAdvisors);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      name: '',
      specialty: '',
      email: '',
      phone: '',
      bio: '',
      image: null,
    });
    setImagePreview(null);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-5xl font-bold mb-2">مدیریت مشاوران علمی</h1>
              <p className="text-xl text-blue-100">افزودن، ویرایش و حذف مشاوران علمی</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition"
            >
              <LogOut size={20} />
              خروج
            </button>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Add Advisor Button */}
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="mb-8 flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-bold hover:from-green-700 hover:to-green-800 transition shadow-lg"
          >
            <Plus size={20} />
            افزودن مشاور جدید
          </button>
        )}

        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingId ? 'ویرایش مشاور' : 'افزودن مشاور جدید'}
              </h2>
              <button
                onClick={handleCancel}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X size={24} className="text-gray-600" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">نام و نام خانوادگی</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="نام مشاور"
                  />
                </div>

                {/* Specialty */}
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">تخصص</label>
                  <input
                    type="text"
                    name="specialty"
                    value={formData.specialty}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="مثال: مهندسی ساختمان"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">ایمیل</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="email@majraye.ir"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">تلفن</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="021-12345678"
                  />
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">بیوگرافی</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                  placeholder="توضیحات کوتاه درباره مشاور"
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">تصویر</label>
                <div className="flex gap-6">
                  <div className="flex-1">
                    <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:bg-blue-50 transition">
                      <Upload size={20} className="text-blue-600" />
                      <span className="text-blue-600 font-semibold">انتخاب تصویر</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {imagePreview && (
                    <div className="w-32 h-32 rounded-lg overflow-hidden border border-gray-300">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-4 pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-bold hover:from-blue-700 hover:to-blue-800 transition"
                >
                  {editingId ? 'ذخیره تغییرات' : 'افزودن مشاور'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition"
                >
                  انصراف
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Advisors Grid */}
        {advisors.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Users size={48} className="mx-auto mb-4 opacity-30" />
            <p>هیچ مشاوری برای نمایش وجود ندارد</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advisors.map(advisor => (
              <div key={advisor.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition">
                {/* Image */}
                {advisor.image ? (
                  <div className="h-48 bg-gray-200 overflow-hidden">
                    <img
                      src={advisor.image}
                      alt={advisor.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="h-48 bg-gradient-to-br from-green-200 to-green-300 flex items-center justify-center">
                    <Users size={48} className="text-green-600 opacity-50" />
                  </div>
                )}

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{advisor.name}</h3>
                  <p className="text-green-600 font-semibold mb-3">{advisor.specialty}</p>
                  
                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <p><span className="font-semibold">ایمیل:</span> {advisor.email}</p>
                    <p><span className="font-semibold">تلفن:</span> {advisor.phone}</p>
                    {advisor.bio && <p><span className="font-semibold">بیوگرافی:</span> {advisor.bio}</p>}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => handleEdit(advisor)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold text-sm"
                    >
                      <Edit2 size={16} />
                      ویرایش
                    </button>
                    <button
                      onClick={() => handleDelete(advisor.id)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold text-sm"
                    >
                      <Trash2 size={16} />
                      حذف
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminScientificAdvisors;
